import { skipToken } from '@apollo/client'
import { isBefore } from 'date-fns'
import { Sparkles } from 'lucide-react'
import { Suspense } from 'react'
import { AGENTS } from '@/components/onboarding-sheet/agents'
import { useOnboardingSheet } from '@/components/onboarding-sheet/context'
import { ProgressCircle } from '@/components/onboarding-sheet/progress-circle'
import { Button } from '@/components/ui/button'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import { gql } from '@/lib/graphql/gql'
import {
  useIdCompany,
  useMixpanel,
  useRBAC,
  useSuspenseQuery,
} from '@/lib/hooks'
import { RoleID } from '@/lib/rbac'
import type {
  GetStartedCompanyCreatedAtQuery,
  GetStartedCompanyCreatedAtQueryVariables,
} from '@/components/onboarding-sheet/get-started-button.types.gen'

const ONBOARDING_LAUNCH_DATE = new Date('2026-05-12T00:00:00Z')

const ONBOARDING_ROLE_IDS: string[] = [
  RoleID.COMPANY_OWNER,
  RoleID.COMPANY_ADMIN,
  RoleID.COMPANY_DEVELOPER,
]

const COMPANY_CREATED_AT_QUERY = gql`
  query GetStartedCompanyCreatedAt($idCompany: String!) {
    company(id: $idCompany) {
      id
      createdAt
    }
  }
`

const PATH_LABELS: Record<string, string> = {
  deploy: 'Deploy application',
  database: 'Deploy database',
}

const GetStartedButtonInner = () => {
  const { idCompany } = useIdCompany()
  const { rbac, rolesInCompany, totalServices } = useRBAC()
  const {
    open,
    totalSteps,
    completedSteps,
    selectedPath,
    selectedAgent,
    isDismissed,
  } = useOnboardingSheet()
  const { track } = useMixpanel()

  const { data } = useSuspenseQuery<
    GetStartedCompanyCreatedAtQuery,
    GetStartedCompanyCreatedAtQueryVariables
  >(
    COMPANY_CREATED_AT_QUERY,
    idCompany ? { variables: { idCompany } } : skipToken
  )

  if (isDismissed) {
    return null
  }

  const isSuperadmin = rbac.kinsta().superadmin.can()
  const hasOnboardingRole = rolesInCompany.some((role) =>
    ONBOARDING_ROLE_IDS.includes(role)
  )

  if (!isSuperadmin && !hasOnboardingRole) {
    return null
  }

  const totalServiceCount =
    totalServices.totalApps +
    totalServices.totalDatabases +
    totalServices.totalStaticSites +
    totalServices.totalObjectStorages +
    totalServices.totalLoadBalancers

  if (totalServiceCount > 0) {
    return null
  }

  const createdAtRaw = data?.company?.createdAt
  const companyCreatedAt = createdAtRaw ? new Date(createdAtRaw) : null
  const isExistingCompany =
    !companyCreatedAt || isBefore(companyCreatedAt, ONBOARDING_LAUNCH_DATE)

  if (isExistingCompany) {
    return null
  }

  const hasPath = totalSteps > 0
  const allDone = hasPath && completedSteps.length >= totalSteps

  const agent =
    selectedPath === 'agent' && selectedAgent
      ? AGENTS.find((a) => a.id === selectedAgent)
      : null

  const label =
    selectedPath === 'agent' && agent
      ? agent.name
      : selectedPath
        ? (PATH_LABELS[selectedPath] ?? 'Get started')
        : 'Get started'

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          onClick={() => {
            track('Onboarding guides visited', { entrypoint: 'dashboard' })
            open(undefined, 'dashboard')
          }}
          className="gap-2 h-8 px-3 text-xs font-medium"
          aria-label={
            hasPath ? 'Continue onboarding' : 'Get started with Sevalla'
          }
        >
          {hasPath ? (
            <ProgressCircle
              total={totalSteps}
              completed={completedSteps.length}
              size={16}
            />
          ) : (
            <Sparkles className="size-3.5" aria-hidden="true" />
          )}
          {label}
          {hasPath && (
            <span className="text-muted-foreground">
              {completedSteps.length}/{totalSteps}
            </span>
          )}
        </Button>
      </TooltipTrigger>
      <TooltipContent>
        {allDone
          ? 'Onboarding complete'
          : hasPath
            ? `${completedSteps.length} of ${totalSteps} steps done`
            : 'Open onboarding guide'}
      </TooltipContent>
    </Tooltip>
  )
}

export const GetStartedButton = () => {
  return (
    <Suspense fallback={null}>
      <GetStartedButtonInner />
    </Suspense>
  )
}
